const FOREX_CODES = [
  'AUDUSD', 'EURUSD', 'GBPUSD', 'USDCHF',
  'USDCNY', 'USDHKD', 'USDKRW', 'USDTHB'
]

const CRYPTO_CODES = [
  'ARBITRUM', 'BINANCE-COIN', 'BITCOIN', 'CARDANO', 'DOGECOIN',
  'ETHEREUM', 'RIPPLE', 'SHIBA-INU', 'SOLANA', 'UNISWAP'
]

const STOCK_CODES = [
  'AAPL', 'AMZN', 'GGLL', 'MSFT', 'NTES', 'NVDS',
  'QABA', 'SLQD', 'SOHU', 'TSL', 'XT'
]

const FUTURES_CODES = [
  'AG8888', 'AL8888', 'AU8888', 'CU8888', 'FU8888', 'NI8888',
  'PB8888', 'RB8888', 'SC8888', 'SN8888', 'SP8888', 'SS8888'
]

const METAL_CODES = [
  'AGAUDO', 'AGCNYO', 'AGEURO', 'AGGBPO',
  'AUCNYO', 'AUEURO', 'AUUSDO'
]

const palettes = {
  1: ['#0e7490', '#083344', '#67e8f9'],
  2: ['#ea580c', '#431407', '#fdba74'],
  3: ['#2563eb', '#172554', '#93c5fd'],
  4: ['#7c3aed', '#2e1065', '#c4b5fd'],
  5: ['#d97706', '#451a03', '#fde68a'],
  0: ['#0891b2', '#042f2e', '#a5f3fc']
}

function cleanCode(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '')
}

function includesCode(codes, code) {
  return codes.indexOf(code) !== -1
}

function resolveType(item) {
  const numericType = Number(item && item.type)
  if (numericType >= 1 && numericType <= 5) {
    return numericType
  }

  const code = cleanCode(item && item.code)
  if (includesCode(FOREX_CODES, code)) return 1
  if (includesCode(CRYPTO_CODES, code)) return 2
  if (includesCode(STOCK_CODES, code)) return 3
  if (includesCode(FUTURES_CODES, code)) return 4
  if (includesCode(METAL_CODES, code)) return 5
  return 0
}

function stockLabel(item, code) {
  const name = String((item && item.appName) || '')
  const match = name.match(/\(([A-Za-z0-9.-]{1,8})\)/)
  if (match) return cleanCode(match[1]).slice(0, 6)

  const aliases = {
    GGLL: 'GOOGL',
    TSL: 'TSLA'
  }
  return aliases[code] || code.slice(0, 6) || 'STK'
}

function iconLabel(item, type) {
  const code = cleanCode(item && item.code)
  const name = String((item && item.appName) || '').trim()

  if (type === 1) {
    const base = name.match(/^([A-Za-z]{3})[\s/]/)
    return base ? base[1].toUpperCase() : code.slice(0, 3)
  }

  if (type === 3) {
    return stockLabel(item, code)
  }

  if (type === 4) {
    return code.replace(/8888$/, '').slice(0, 4) || 'FUT'
  }

  if (type === 5) {
    const metal = code.slice(0, 2) === 'AU' ? 'Au' : 'Ag'
    const currency = code.slice(2, 5)
    return currency ? `${metal}/${currency}` : metal
  }

  return code.slice(0, 5) || 'BX'
}

function fontSize(label) {
  if (label.length <= 2) return 24
  if (label.length <= 3) return 20
  if (label.length <= 5) return 15
  return 11
}

function svgDataUri(item, type) {
  const label = iconLabel(item, type)
  const colors = palettes[type] || palettes[0]
  const size = fontSize(label)

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="${colors[0]}"/>
          <stop offset="1" stop-color="${colors[1]}"/>
        </linearGradient>
        <linearGradient id="s" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="${colors[2]}" stop-opacity="0"/>
          <stop offset="0.5" stop-color="${colors[2]}" stop-opacity="0.9"/>
          <stop offset="1" stop-color="${colors[2]}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="62" height="62" rx="18" fill="url(#g)"/>
      <rect x="2" y="2" width="60" height="60" rx="17" fill="none" stroke="${colors[2]}" stroke-opacity="0.45"/>
      <circle cx="49" cy="15" r="10" fill="${colors[2]}" fill-opacity="0.11"/>
      <path d="M8 47 L18 38 L27 42 L38 27 L47 32 L56 18" fill="none" stroke="${colors[2]}" stroke-opacity="0.24" stroke-width="2"/>
      <rect x="8" y="50" width="48" height="1.4" rx="0.7" fill="url(#s)"/>
      <text x="32" y="38" text-anchor="middle" fill="#ffffff" font-size="${size}" font-weight="700" font-family="Arial,Helvetica,sans-serif">${label}</text>
    </svg>
  `

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

function hasLogo(item) {
  return Boolean(item && typeof item.logo === 'string' && item.logo.trim())
}

export function normalizeMarketItem(item) {
  if (!item || typeof item !== 'object') return item

  const normalized = { ...item }
  const type = resolveType(normalized)

  // Keep the ten working crypto logos. All other product categories use a
  // self-contained local vector icon and no longer depend on retired OSS URLs.
  if (type !== 2 || !hasLogo(normalized)) {
    normalized.logo = svgDataUri(normalized, type)
  }

  return normalized
}

export function normalizeMarketResponse(response) {
  if (!response || typeof response !== 'object') return response

  if (Array.isArray(response.result)) {
    response.result = response.result.map(normalizeMarketItem)
  } else if (response.result && typeof response.result === 'object') {
    response.result = normalizeMarketItem(response.result)
  }

  return response
}
